import { useState } from "react";
import { Swords, ChevronDown, Copy, Check, Gauge as GaugeIcon } from "lucide-react";
import { MarkdownView } from "./MarkdownView";
import { normalizeMarkdown } from "@/lib/markdown";
import type { Battlecard as BattlecardData } from "@/lib/swarmTypes";

/**
 * Battlecard - a collapsible, battle-ready competitive brief.
 *
 * Renders `markdown` through the SAME ReactMarkdown + remark-gfm +
 * normalizeMarkdown pipeline (and cyan-link styling) as the agent cards, so
 * `[text](url)` sources are clickable cyan links. Expanded by default so the
 * brief is visible without a click (with a toggle to collapse); the header
 * carries a Copy button (copies the normalised markdown) and a confidence chip.
 */

interface BattlecardProps {
  battlecard: BattlecardData | null;
  competitor?: string;
  /** True while the swarm is still running - drives the assembling skeleton. */
  isRunning?: boolean;
}

export function Battlecard({ battlecard, competitor, isRunning }: BattlecardProps) {
  // Open by default so the battle-ready brief is visible without a click (like
  // the ThreatPanel, which renders immediately). The toggle still collapses it.
  const [open, setOpen] = useState(true);
  const [copied, setCopied] = useState(false);

  if (!battlecard || !battlecard.markdown.trim()) {
    // Still working - show the battlecard's OWN assembling state (NOT the threat
    // panel's "Fusing signals…" copy). Once nothing is in flight, render nothing.
    return isRunning ? <BattlecardSkeleton competitor={competitor} /> : null;
  }

  const normalized = normalizeMarkdown(battlecard.markdown);
  const bodyId = "battlecard-body";

  async function copyCard() {
    try {
      await navigator.clipboard.writeText(normalized);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable - no-op */
    }
  }

  return (
    <section className="ns-glass ns-reveal rounded-2xl overflow-hidden shadow-card">
      {/* ── Header (the collapse toggle) ── */}
      <div className="flex items-center gap-3 px-5 py-4">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={bodyId}
          className="flex items-center gap-3 flex-1 min-w-0 text-left cursor-pointer group"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-emerald-700/40 bg-emerald-500/10 text-emerald-300 shrink-0">
            <Swords className="w-4 h-4" aria-hidden="true" />
          </span>
          <span className="min-w-0">
            <span className="block text-[10px] font-semibold text-emerald-300 uppercase tracking-[0.2em]">
              Battlecard
            </span>
            <span className="block font-mono text-base font-bold text-white truncate leading-tight group-hover:text-cyan-300 transition-colors">
              {competitor ? `How to win vs ${competitor}` : "How to win"}
            </span>
          </span>
        </button>

        <div className="flex items-center gap-1.5 shrink-0">
          <ConfidenceChip value={battlecard.confidence} />
          {open && (
            <button
              type="button"
              onClick={copyCard}
              title="Copy battlecard"
              aria-label="Copy battlecard to clipboard"
              className="inline-flex items-center gap-1.5 px-2 py-1.5 text-xs text-slate-400 hover:text-[#22d3ee] hover:bg-white/5 rounded-lg transition-colors cursor-pointer min-h-[36px]"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{copied ? "Copied" : "Copy"}</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls={bodyId}
            aria-label={open ? "Collapse battlecard" : "Expand battlecard"}
            className="inline-flex items-center justify-center h-9 w-9 rounded-lg text-slate-400 hover:text-slate-100 hover:bg-white/5 transition-colors cursor-pointer"
          >
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      {/* ── Body (markdown) ── */}
      {open && (
        <div
          id={bodyId}
          className="px-5 pb-5 border-t border-white/10 pt-4 max-h-[60vh] overflow-y-auto scrollbar-thin select-text"
        >
          <MarkdownView variant="compact">{battlecard.markdown}</MarkdownView>
        </div>
      )}
    </section>
  );
}

function ConfidenceChip({ value }: { value: number }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2 py-1 text-[10px] font-mono text-slate-400"
      title="Confidence in this battlecard"
    >
      <GaugeIcon className="w-3 h-3 text-slate-500" aria-hidden="true" />
      {value}% conf
    </span>
  );
}

// ── Skeleton (while running, before the battlecard arrives) ───────────────────
// Carries the battlecard's OWN loading copy ("Assembling your battlecard…"),
// distinct from the threat panel's "Fusing signals…" line.
function BattlecardSkeleton({ competitor }: { competitor?: string }) {
  return (
    <section
      className="ns-glass rounded-2xl overflow-hidden shadow-card"
      role="status"
      aria-label="Assembling your battlecard"
    >
      <div className="flex items-center gap-3 px-5 py-4">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-emerald-700/40 bg-emerald-500/10 text-emerald-300 shrink-0">
          <Swords className="w-4 h-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <span className="block text-[10px] font-semibold text-emerald-300 uppercase tracking-[0.2em]">
            Battlecard
          </span>
          <span className="block font-mono text-base font-bold text-white truncate leading-tight">
            {competitor ? `How to win vs ${competitor}` : "How to win"}
          </span>
        </div>
        <span className="flex items-center gap-1.5 text-[11px] text-emerald-300/80 shrink-0">
          <span className="flex gap-0.5 items-end h-3" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="w-0.5 bg-emerald-400/60 rounded-full animate-wave"
                style={{ height: "100%", animationDelay: `${i * 0.12}s` }}
              />
            ))}
          </span>
          Assembling your battlecard…
        </span>
      </div>
      <div className="px-5 pb-5 border-t border-white/10 pt-4 space-y-2.5">
        {["94%", "82%", "88%", "70%"].map((w, i) => (
          <div
            key={i}
            className="h-2.5 rounded bg-white/5 animate-pulse"
            style={{ width: w, animationDelay: `${i * 0.1}s` }}
          />
        ))}
      </div>
    </section>
  );
}
